/*
 * F-03 · MARCAS DE TIEMPO DE LA PANTALLA SESIÓN (RF-218, RN-617, D-318).
 *
 * `Nucleo.Usuarios.FechaUltimoAcceso` existe desde el 1/6 y se escribe en cada
 * inicio de sesión. Su destino estaba escrito en el comentario de la columna desde
 * el primer día: «se presenta convertida a la zona detectada del dispositivo»
 * (RNF-003). Esto es esa presentación, y nada más: aquí no se calcula ninguna
 * fecha, no se inventa ninguna hora y no se consulta nada.
 *
 * QUIEN NUNCA HA ENTRADO NO TIENE FECHA, y entonces se dice «Aún no entra» — ni
 * una fecha falsa, ni un guion suelto que no explique nada (RN-617).
 *
 * Funciones puras, sin React y sin red: el Tester las prueba sin montar pantalla.
 */

/** Lo que se dice de quien nunca ha iniciado sesión. */
export const NUNCA_HA_ENTRADO = 'Aún no entra';

/**
 * Instante UTC → «dd/mm/aaaa a las hh:mm» EN LA ZONA DETECTADA DEL DISPOSITIVO,
 * con minutos de precisión (RN-617). Devuelve null si no hay instante que mostrar:
 * quien llama decide el texto, para que esta función no tome decisiones de pantalla.
 */
export function marcaDeTiempoLocal(instanteUtc) {
  if (!instanteUtc) return null;

  const fecha = new Date(instanteUtc);
  if (Number.isNaN(fecha.getTime())) return null;

  const dosDigitos = (numero) => String(numero).padStart(2, '0');

  // Componentes LOCALES del dispositivo, nunca UTC: es la misma disciplina de
  // D-06 y la razón por la que no se usa `toISOString()` aquí.
  const dia = dosDigitos(fecha.getDate());
  const mes = dosDigitos(fecha.getMonth() + 1);
  const anio = fecha.getFullYear();
  const hora = dosDigitos(fecha.getHours());
  const minuto = dosDigitos(fecha.getMinutes());

  return `${dia}/${mes}/${anio} a las ${hora}:${minuto}`;
}

/**
 * RF-218(a) · El subtítulo de UNA persona del hogar, armado completo:
 *   · persona en sesión → «Clave: deisy · Sesión iniciada el 08/10/2026 a las 14:30»
 *   · la otra persona   → «Clave: mario · Acceso activo · Última entrada el …»
 *   · sin acceso        → «Clave: mario · Sin acceso · Última entrada el …»
 *   · nunca entró       → «… · Aún no entra»
 *
 * La marca de la OTRA persona se ve a propósito: la pantalla aprobada la dibuja y
 * la visibilidad total del hogar es decisión vigente (RN-027). No es dato de
 * credencial: es cuándo entró, no con qué entró.
 */
export function subtituloDePersona(usuario) {
  const partes = [`Clave: ${usuario?.clave ?? ''}`];

  if (usuario?.esUsuarioEnSesion) {
    const marca = marcaDeTiempoLocal(usuario?.fechaUltimoAcceso);
    partes.push(marca ? `Sesión iniciada el ${marca}` : NUNCA_HA_ENTRADO);
    return partes.join(' · ');
  }

  partes.push(usuario?.activo ? 'Acceso activo' : 'Sin acceso');

  const marca = marcaDeTiempoLocal(usuario?.fechaUltimoAcceso);
  partes.push(marca ? `Última entrada el ${marca}` : NUNCA_HA_ENTRADO);
  return partes.join(' · ');
}

/** Iniciales para la insignia del renglón, hasta dos palabras. */
export function inicialesDePersona(nombre) {
  return String(nombre ?? '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0].toUpperCase())
    .join('') || '?';
}
